<template>
  <div id="app">
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import sessionService from './services/session.service';

export default {
  name: 'App',
  computed: {
    ...mapGetters({
      isAuthenticated: 'auth/isAuthenticated'
    })
  },
  setup() {
    const router = useRouter();

    onMounted(() => {
      // Verificação da sessão ao carregar o aplicativo
      const token = sessionService.getToken();
      
      // Se o token existir, garantir que o router sabe disso
      if (token) {
      } else {
        
        // Se estiver em uma rota protegida, redirecionar para login
        if (router.currentRoute.value.meta.requiresAuth) {
          router.push('/login');
        }
      }
    });
  }
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Manrope:wght@400;500;600;700;800&family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');
@import 'primevue/resources/themes/lara-light-blue/theme.css';
@import 'primevue/resources/primevue.min.css';
@import 'primeicons/primeicons.css';
@import 'primeflex/primeflex.css';

// Retorna design system: "The Bound Appointment Ledger" (see DESIGN.md at the project
// root for the full system). Variable NAMES below are kept stable — dozens of pages
// still reference them by these names — but every VALUE now maps to the ledger palette.
// data-theme='light' is retired as a distinct look (see MainLayout.vue for the toggle
// itself): it resolves to the exact same values so a stray toggle can't reintroduce the
// old dark-navy/neon world.
:root, :root[data-theme='light'] {
  --ink: #F1E9D8;           /* was darkest bg; now the page's paper ground */
  --ink-soft: #E4D8BC;      /* was secondary dark bg; now the deeper paper panel tone */
  --surface: #F1E9D8;       /* was translucent glass card bg; now flat paper (no blur) */
  --line: #C7B996;          /* ledger rule/hairline — the most-used token in this file */
  --text: #211C17;          /* ledger ink */
  --muted: #4A4136;         /* soft ink */
  --lime: #B33A2C;          /* the one stamp-red accent */
  --aqua: #211C17;          /* was a second accent color; now ink, so no second color competes with the stamp */
  --violet: #4A4136;
  --coral: #B33A2C;
  --glow: none;             /* No-Shadow Rule: depth comes from ruled hairlines only */
  --surface-ground: #F1E9D8;
  --surface-section: #E4D8BC;
  --surface-card: #F1E9D8;
  --surface-overlay: #F1E9D8;
  --surface-hover: #E4D8BC;
  --surface-border: #C7B996;
  --text-color: #211C17;
  --text-color-secondary: #4A4136;
  --primary-color: #211C17;
  --primary-color-text: #F1E9D8;

  // Aliases matching DESIGN.md's own frontmatter token names verbatim (paper, paper-deep,
  // rule, stamp-red, accent). Several restyled pages reference these names directly rather
  // than the legacy --ink/--ink-soft/--line/--lime names above — both must resolve to the
  // same values, so neither convention silently renders as an undefined custom property.
  --paper: #F1E9D8;
  --paper-deep: #E4D8BC;
  --rule: #C7B996;
  --stamp-red: #B33A2C;
  --accent: #B33A2C;
}

* { box-sizing: border-box; }

html, body {
  margin: 0;
  padding: 0;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: var(--ink);
  color: var(--text);
}

#app {
  min-height: 100vh;
  background: var(--ink);
}

.main-layout, .main-layout .app-content { background: var(--ink) !important; }

.fade-enter-active { transition: opacity .32s ease, transform .32s cubic-bezier(.2,.8,.2,1); }
.fade-leave-active { transition: opacity .16s ease; }
.fade-enter-from { opacity: 0; transform: translateY(10px); }
.fade-leave-to { opacity: 0; }

.p-component { font-family: 'IBM Plex Sans', sans-serif; }
.p-button { border-radius: 0; font-weight: 600; transition: background .15s ease; }
.p-button:not(:disabled):active { transform: none; }
.p-inputtext, .p-dropdown, .p-multiselect, .p-calendar .p-inputtext {
  border-radius: 0; border: none; border-bottom: 1px solid var(--line); background: transparent; color: var(--text);
}
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; scroll-behavior: auto !important; }
}

/* System bridge: normalizes legacy page modules into one responsive ledger surface.
   Flat paper, ruled hairlines, sharp corners, no shadow/blur/gradient anywhere — see
   DESIGN.md's No-Shadow Rule and Shapes section. */
.app-content [class*="-page"], .app-content .page-container { color: var(--text); }
.app-content [class*="-page"] > [class*="header"], .app-content [class*="-page"] .page-header,
.app-content [class*="-page"] .dashboard-header { border-bottom: 2px solid var(--text); padding-bottom: 16px; }
.app-content [class*="-page"] h1, .app-content [class*="-page"] h2, .app-content [class*="-page"] h3 { color: var(--text) !important; letter-spacing: -.01em; font-family: 'IBM Plex Sans', sans-serif; }
.app-content [class*="-page"] p, .app-content [class*="-page"] label, .app-content [class*="-page"] .p-datatable { color: var(--muted); }
.app-content .card, .app-content [class*="-card"], .app-content [class*="-panel"], .app-content [class*="-section"],
.app-content .p-card, .app-content .p-dialog .p-dialog-content, .app-content .p-datatable-wrapper {
  border: 1px solid var(--line) !important; background: var(--ink) !important;
  box-shadow: none !important; border-radius: 0 !important; backdrop-filter: none;
}
.app-content .p-datatable .p-datatable-thead > tr > th { background: var(--ink-soft) !important; color: var(--muted) !important; border-color: var(--line) !important; font: 600 .65rem 'IBM Plex Mono', monospace; letter-spacing: .08em; text-transform: uppercase; }
.app-content .p-datatable .p-datatable-tbody > tr { background: transparent !important; color: var(--text) !important; transition: background .15s ease; }
.app-content .p-datatable .p-datatable-tbody > tr:hover { background: var(--ink-soft) !important; }
.app-content .p-datatable .p-datatable-tbody > tr > td { border-color: var(--line) !important; }
.app-content .p-tabview .p-tabview-nav, .app-content .p-tabmenu .p-tabmenu-nav { background: transparent !important; border-color: var(--line) !important; }
.app-content .p-tabview .p-tabview-panels, .app-content .p-tabmenu .p-tabmenuitem .p-menuitem-link { background: transparent !important; color: var(--muted) !important; }
.app-content .p-dialog .p-dialog-header, .app-content .p-dialog .p-dialog-footer { background: var(--ink) !important; color: var(--text) !important; border-color: var(--line) !important; }
.app-content .p-dialog { border-radius: 0 !important; overflow: hidden; border: 1px solid var(--line); box-shadow: none !important; }
.app-content .p-tag { border-radius: 0; font: 600 .65rem 'IBM Plex Mono', monospace; }
.app-content .p-button:not(.p-button-text):not(.p-button-outlined) { background: var(--text); color: var(--ink); border: 0; box-shadow: none; }
.app-content .p-button:not(.p-button-text):not(.p-button-outlined):hover { transform: none; background: var(--muted); }
.app-content .p-button.p-button-outlined { color: var(--text); background: transparent; border-color: var(--line); box-shadow: none; }
.app-content .p-button.p-button-outlined:hover { color: var(--text); background: var(--ink-soft); border-color: var(--text); }
.app-content .p-inputtext, .app-content .p-dropdown, .app-content .p-multiselect, .app-content .p-calendar .p-inputtext { min-height: 42px; }
.app-content .p-inputtext::placeholder { color: var(--muted); opacity: 1; }

/* Wide displays should feel like a command center, not a narrow mobile page
   floating in an empty canvas. Individual modules may still set tighter text
   measures inside their own content. */
.app-content > * { width: min(100%, 1920px); margin-inline: auto; }
.app-content .dashboard-page, .app-content .workspace-page, .app-content .invoices-page { max-width: none !important; }
@media (min-width: 1800px) { .app-content { padding-inline: clamp(28px, 3vw, 64px) !important; } }

/* Only legacy Prime surfaces are normalized here. Component-specific cards own their visual hierarchy. */
.app-content [class*="-page"] .stat-value, .app-content [class*="-page"] .stat-label,
.app-content [class*="-page"] .description, .app-content [class*="-page"] .name,
.app-content [class*="-page"] .time, .app-content [class*="-page"] .execution-info,
.app-content [class*="-page"] .trigger-info { color: var(--text) !important; }
.app-content [class*="-page"] .error-details { background: var(--ink-soft) !important; color: var(--lime) !important; }

/* Kanban: ruled ledger columns, flat cards, status color lives only in the column header. */
.kanban-page .kanban-board .kanban-column { background: var(--ink-soft) !important; border: 1px solid var(--line); border-radius: 0 !important; max-height: none !important; min-height: 440px; overflow: hidden; }
.kanban-page .kanban-board .column-content { background: transparent !important; }
.kanban-page .kanban-board .lead-card { background: var(--ink) !important; border: 1px solid var(--line); border-radius: 0 !important; box-shadow: none !important; }
.kanban-page .kanban-board .lead-name, .kanban-page .kanban-board .lead-info, .kanban-page .kanban-board .lead-card-footer { color: var(--text) !important; }
.kanban-page .kanban-board .lead-info i, .kanban-page .kanban-board .empty-column, .kanban-page .kanban-board .column-footer { color: var(--muted) !important; }
.kanban-page .kanban-board .column-footer { border-color: var(--line) !important; }
.kanban-page .kanban-board .empty-column { min-height: 220px; }

@media (max-width: 1100px) {
  .app-content { padding: 18px !important; }
  .app-content [class*="-page"] [class*="header"] { gap: 12px; flex-wrap: wrap; }
  .app-content .p-datatable-wrapper { overflow-x: auto; }
}
@media (max-width: 700px) {
  .app-content { padding: 14px !important; }
  .app-content [class*="-page"] { min-width: 0; }
  .app-content [class*="-page"] [class*="header"], .app-content [class*="-page"] [class*="toolbar"], .app-content [class*="-page"] [class*="actions"] { align-items: stretch !important; flex-direction: column; }
  .app-content [class*="-page"] [class*="filters"], .app-content [class*="-page"] [class*="form-grid"] { grid-template-columns: 1fr !important; }
  .app-content .p-button { width: 100%; justify-content: center; }
  .app-content .p-dialog { width: calc(100vw - 24px) !important; max-height: calc(100vh - 24px); }
  .app-content .p-datatable { font-size: .78rem; }
  .app-content .p-tabview .p-tabview-nav, .app-content .p-tabmenu .p-tabmenu-nav { overflow-x: auto; flex-wrap: nowrap; scrollbar-width: none; }
  .app-content .p-tabview .p-tabview-nav::-webkit-scrollbar, .app-content .p-tabmenu .p-tabmenu-nav::-webkit-scrollbar { display: none; }
  .app-content .p-tabview .p-tabview-nav li, .app-content .p-tabmenu .p-tabmenu-nav li { flex: 0 0 auto; }
  .app-content .p-datatable-wrapper { max-width: 100%; border-radius: 0 !important; }
  .app-content .p-datatable .p-datatable-thead > tr > th { white-space: nowrap; }
  .app-content .p-paginator { flex-wrap: wrap; gap: 3px; justify-content: center; }
}

@import './assets/platform-theme.scss';
</style>
